import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { MessageSquare } from "lucide-react";

export const ContactTransporterDialog = ({ transporterId, transporterName, trigger }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company_name: "", pickup_location: "", delivery_location: "", message: "", contact_back: "" });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.message.trim()) { toast.error("Please add a message"); return; }
    setSaving(true);
    try {
      await api.post(`/transporters/${transporterId}/contact`, form);
      toast.success("Request sent through the platform");
      setOpen(false);
      setForm({ company_name: "", pickup_location: "", delivery_location: "", message: "", contact_back: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send request");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="rounded-none bg-primary hover:bg-primary/90 w-full" data-testid={`contact-transporter-${transporterId}`}>
            <MessageSquare className="h-4 w-4 mr-1" /> Request quote
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader><DialogTitle className="font-display">Request a quote from {transporterName}</DialogTitle></DialogHeader>
        {!user ? (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Log in to send a quote request through the platform. Your contact details stay private.</p>
            <Link to="/login"><Button className="rounded-none bg-primary hover:bg-primary/90 w-full" data-testid="contact-login-redirect">Log in to continue</Button></Link>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Your request is sent securely through WILTOM TradeLink. The transporter will respond on the platform.</p>
              <div><Label className="label-caps">Your company (optional)</Label><Input value={form.company_name} onChange={upd("company_name")} className="rounded-none mt-1 h-10" data-testid="contact-company" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Pickup</Label><Input value={form.pickup_location} onChange={upd("pickup_location")} className="rounded-none mt-1 h-10" data-testid="contact-pickup" /></div>
                <div><Label className="label-caps">Delivery</Label><Input value={form.delivery_location} onChange={upd("delivery_location")} className="rounded-none mt-1 h-10" data-testid="contact-delivery" /></div>
              </div>
              <div><Label className="label-caps">Message</Label><Textarea value={form.message} onChange={upd("message")} rows={3} className="rounded-none mt-1" placeholder="Describe the load, dates and any requirements…" data-testid="contact-message" /></div>
              <div><Label className="label-caps">How should they reach you back?</Label><Input value={form.contact_back} onChange={upd("contact_back")} className="rounded-none mt-1 h-10" placeholder="Platform message / phone / email" data-testid="contact-back" /></div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90" data-testid="submit-contact-btn">{saving ? "Sending…" : "Send request"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
